/*eslint-disable*/

/**
 * Escape string for use inside double-quoted HTML attributes (e.g. value, data-*).
 * Prevents values like 17.71"L x 17.71"W from breaking attribute parsing.
 */
function escapeHtmlAttribute(str) {
    if (str == null || str === undefined) return '';
    return String(str)
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
}

/** Escape string for safe text/HTML content (e.g. label text). */
function escapeHtmlText(str) {
    if (str == null || str === undefined) return '';
    return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
}

export default function variantPicker() {
    if (!customElements.get('parent-sku-variant-selects')) {
        customElements.define(
            'parent-sku-variant-selects',
            class ParentSkuVariantSelects extends HTMLElement {
                constructor() {
                    super();
                    this.currentProduct = null;
                    this.parentSku = null;
                    this.relatedProducts = [];
                    this.pickerSettings = null;
                    this.updateUrl = true;
                }

                async connectedCallback() {
                    this.currentProductId = parseInt(this.dataset.currentProductId);
                    this.currentProductTitle = this.dataset.currentProductTitle;
                    this.parentSku = this.dataset.parentSku;
                    this.updateUrl = this.dataset.updateUrl !== 'false';
                    
                    await this.loadCurrentProduct();
                    await this.loadPickerSettings();
                    await this.findRelatedProducts();
                    this.renderVariantPicker();
                    this.setupEventListeners();
                }

                loadCurrentProduct() {
                    const currentProductScript = this.querySelector('script[data-current-product]');
                    if (currentProductScript) {
                        try {
                            this.currentProduct = JSON.parse(currentProductScript.textContent);
                        } catch (e) {
                            console.error('Failed to parse current product data:', e);
                        }
                    }
                }

                loadPickerSettings() {
                    const pickerSettingsScript = this.querySelector('script[data-picker-settings]');
                    if (pickerSettingsScript) {
                        try {
                            this.pickerSettings = JSON.parse(pickerSettingsScript.textContent);
                        } catch (e) {
                            console.error('Failed to parse picker settings:', e);
                        }
                    }
                }

                async findRelatedProducts() {
                    try {
                        // Use GraphQL to search for products with the same title and get variant metafields
                        const query = `
                            query findRelatedProducts($parentSku: String!) {
                                collection(handle: "mr-all-products") {
                                    products(first: 50, filters: {
                                        variantMetafield: {
                                            namespace: "custom",
                                            key: "parent_sku",
                                            value: $parentSku
                                        }
                                    }) {
                                        edges {
                                            node {
                                                id
                                                handle
                                                title
                                                options {
                                                    id
                                                    name
                                                    values
                                                }
                                                variants(first: 100) {
                                                    edges {
                                                        node {
                                                            id
                                                            title
                                                            availableForSale
                                                            selectedOptions {
                                                                name
                                                                value
                                                            }
                                                            metafields(identifiers: [{namespace: "custom", key: "parent_sku"}]) {
                                                                namespace
                                                                key
                                                                value
                                                            }
                                                        }
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        `;

                        const variables = {
                            parentSku: this.parentSku
                        };

                        const response = await fetch('/api/2025-04/graphql.json', {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'X-Shopify-Storefront-Access-Token': storefrontAccessToken
                            },
                            body: JSON.stringify({ query, variables })
                        });

                        const data = await response.json();
                        
                        if (data.errors) {
                            console.error('GraphQL errors:', data.errors);
                            throw new Error('GraphQL query failed');
                        }

                        // Process the GraphQL response
                        this.relatedProducts = []; // Include current product
                        
                        if (data.data?.collection?.products?.edges) {
                            for (const productEdge of data.data.collection.products.edges) {
                                const product = productEdge.node;
                                
                                // Check if this product has variants with matching parent_sku
                                const hasMatchingParentSku = product.variants.edges.some(variantEdge => {
                                    const variant = variantEdge.node;
                                    const parentSkuMetafield = variant.metafields.find(
                                        mf => mf?.namespace === 'custom' && mf?.key === 'parent_sku'
                                    );
                                    return parentSkuMetafield?.value === this.parentSku;
                                });
                                
                                if (hasMatchingParentSku) {
                                    // Convert GraphQL format to the format expected by our existing code
                                    const convertedProduct = this.convertGraphQLProduct(product);
                                    this.relatedProducts.push(convertedProduct);
                                }
                            }
                        }

                        console.log('Found related products via GraphQL:', this.relatedProducts);
                        
                    } catch (error) {
                        console.error('Error finding related products via GraphQL:', error);
                    }
                }

                convertGraphQLProduct(graphqlProduct) {
                    // Convert GraphQL product format to match the REST API format
                    const variants = graphqlProduct.variants.edges.map(variantEdge => {
                        const variant = variantEdge.node;
                        const parentSkuMetafield = variant.metafields.find(
                            mf => mf.namespace === 'custom' && mf.key === 'parent_sku'
                        );
                        
                        return {
                            id: parseInt(variant.id.split('/').pop()), // Extract numeric ID from GraphQL ID
                            title: variant.title,
                            available: variant.availableForSale,
                            options: variant.selectedOptions.map(option => option.value),
                            metafields: {
                                custom: {
                                    parent_sku: parentSkuMetafield?.value || null
                                }
                            }
                        };
                    });

                    return {
                        id: parseInt(graphqlProduct.id.split('/').pop()), // Extract numeric ID from GraphQL ID
                        handle: graphqlProduct.handle,
                        title: graphqlProduct.title,
                        options: graphqlProduct.options.map(option => option.name),
                        variants: variants
                    };
                }

                renderVariantPicker() {
                    if (!this.pickerSettings || this.relatedProducts.length === 0) {
                        console.error('Missing data for rendering variant picker');
                        return;
                    }

                    // Collect all unique options from related products
                    const allOptions = this.collectAllOptions();
                    
                    // Generate HTML for each option
                    const optionsHtml = allOptions.map(option => this.renderOption(option)).join('');
                    
                    // Update the loading placeholder
                    const loadingContainer = this.querySelector(`#parent-sku-options-${this.pickerSettings.section_id}`);
                    if (loadingContainer) {
                        loadingContainer.innerHTML = optionsHtml;
                        this.syncOptionControlValuesFromDataAttributes(loadingContainer);
                        loadingContainer.classList.remove('parent-sku-options-loading');
                    }
                }

                /**
                 * After innerHTML parse, force .value from data-option-value so quoted strings
                 * always match Shopify option strings (handles any parser edge cases).
                 */
                syncOptionControlValuesFromDataAttributes(container) {
                    container.querySelectorAll('input[type="radio"][data-option-value]').forEach((input) => {
                        const full = input.getAttribute('data-option-value');
                        if (full != null && full !== '') {
                            input.value = full;
                        }
                    });
                    container.querySelectorAll('select option[data-option-value]').forEach((opt) => {
                        const full = opt.getAttribute('data-option-value');
                        if (full != null && full !== '') {
                            opt.value = full;
                        }
                    });
                }

                collectAllOptions() {
                    const optionsMap = new Map();
                    
                    for (const product of this.relatedProducts) {
                        if (!product.options || !product.variants) continue;
                        
                        // Only process variants that have the matching parent_sku
                        const relevantVariants = product.variants.filter(variant => 
                            variant.metafields?.custom?.parent_sku === this.parentSku
                        );
                        
                        for (let i = 0; i < product.options.length; i++) {
                            const optionName = product.options[i];
                            const optionKey = optionName.toLowerCase();
                            
                            if (!optionsMap.has(optionKey)) {
                                optionsMap.set(optionKey, {
                                    name: optionName,
                                    values: new Set(),
                                    currentValue: null
                                });
                            }
                            
                            const option = optionsMap.get(optionKey);
                            
                            // Add values from relevant variants only
                            for (const variant of relevantVariants) {
                                if (variant.options && variant.options[i]) {
                                    option.values.add(variant.options[i]);
                                    
                                    // Set current value if this is from the current product
                                    if (product.id === this.currentProduct.id && variant.available) {
                                        // Find the currently selected variant or first available
                                        const currentVariant = this.currentProduct.variants.find(v => v.id === variant.id);
                                        if (currentVariant && (currentVariant.id === this.currentProduct.selected_or_first_available_variant?.id || !option.currentValue)) {
                                            option.currentValue = variant.options[i];
                                        }
                                    }
                                }
                            }
                        }
                    }
                    
                    return Array.from(optionsMap.values()).map(option => ({
                        ...option,
                        values: Array.from(option.values)
                    }));
                }

                renderOption(option) {
                    const { picker_type, swatch_type, button_style, button_size, section_id, product_form_id } = this.pickerSettings;
                    
                    let pickerClass = picker_type;
                    const isFieldset = ['swatch', 'color', 'button'].includes(picker_type);
                    const tag = isFieldset ? 'fieldset' : 'div';
                    const labelTag = isFieldset ? 'legend' : 'label';
                    
                    let wrapClass = `product-form__input product-form__input--${picker_type}`;
                    if (isFieldset) {
                        wrapClass += ` js flex flex-wrap gap-${picker_type === 'button' ? '3' : '4'} product-form__input--button-${button_style}`;
                    }
                    
                    const optionHtml = `
                        <${tag} class="${wrapClass}" style="--swatch-size: ${button_size}px;">
                            <${labelTag} class="form__label">
                                <div class="flex flex-wrap gap-2">
                                    <span class="font-body-bold" style="font-weight:500;">${escapeHtmlText(option.name)}:</span>
                                    <span data-selected-swatch-value="${escapeHtmlAttribute(option.name)}">
                                        ${escapeHtmlText(option.currentValue || option.values[0] || '')}
                                    </span>
                                </div>
                            </${labelTag}>
                            ${this.renderOptionValues(option, picker_type, section_id, product_form_id)}
                        </${tag}>
                    `;
                    
                    return optionHtml;
                }

                renderOptionValues(option, pickerType, sectionId, productFormId) {
                    if (['swatch', 'color', 'button'].includes(pickerType)) {
                        return option.values.map((value, index) => 
                            this.renderRadioOption(option, value, index, pickerType, sectionId, productFormId)
                        ).join('');
                    } else {
                        return this.renderSelectOption(option, pickerType, sectionId, productFormId);
                    }
                }

                renderRadioOption(option, value, index, pickerType, sectionId, productFormId) {
                    const inputId = `ParentSkuOption-${sectionId}-${option.name}-${index}`;
                    const inputName = `${option.name}-parent-sku`;
                    const isChecked = value === option.currentValue;
                    const matchingProduct = this.findProductForOptionValue(option.name, value);
                    const isDisabled = !matchingProduct;
                    
                    return `
                        <input
                            type="radio"
                            id="${inputId}"
                            name="${escapeHtmlAttribute(inputName)}"
                            value="${escapeHtmlAttribute(value)}"
                            form="${escapeHtmlAttribute(productFormId)}"
                            ${isChecked ? 'checked' : ''}
                            ${isDisabled ? 'class="disabled"' : ''}
                            data-option-name="${escapeHtmlAttribute(option.name)}"
                            data-option-value="${escapeHtmlAttribute(value)}"
                            data-product-id="${matchingProduct?.id || ''}"
                            data-product-handle="${escapeHtmlAttribute(matchingProduct?.handle || '')}"
                        >
                        <label for="${inputId}">
                            ${escapeHtmlText(value)}
                            ${isDisabled ? '<span class="visually-hidden label-unavailable">Unavailable</span>' : ''}
                        </label>
                    `;
                }

                renderSelectOption(option, pickerType, sectionId, productFormId) {
                    const selectId = `ParentSkuOption-${sectionId}-${option.name}`;
                    const optionsHtml = option.values.map(value => {
                        const matchingProduct = this.findProductForOptionValue(option.name, value);
                        const isSelected = value === option.currentValue;
                        const isDisabled = !matchingProduct;
                        
                        return `
                            <option
                                value="${escapeHtmlAttribute(value)}"
                                ${isSelected ? 'selected="selected"' : ''}
                                data-option-name="${escapeHtmlAttribute(option.name)}"
                                data-option-value="${escapeHtmlAttribute(value)}"
                                data-product-id="${matchingProduct?.id || ''}"
                                data-product-handle="${escapeHtmlAttribute(matchingProduct?.handle || '')}"
                            >
                                ${isDisabled ? `${escapeHtmlText(value)} - Unavailable` : escapeHtmlText(value)}
                            </option>
                        `;
                    }).join('');

                    return `
                        <div class="select">
                            <select
                                id="${selectId}"
                                class="form-control form-control--select"
                                name="options[${escapeHtmlAttribute(option.name)}]"
                                form="${escapeHtmlAttribute(productFormId)}"
                                data-option-name="${escapeHtmlAttribute(option.name)}"
                            >
                                ${optionsHtml}
                            </select>
                        <svg xmlns="http://www.w3.org/2000/svg" class="icon icon-caret-down icon--medium" viewBox="0 0 20 20" fill="none">
                            <path d="M16.25 7.5L10 13.75L3.75 7.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path>
                        </svg>
                        </div>
                    `;
                }

                /**
                 * Read canonical option value from a control (avoids truncated .value when markup was wrong).
                 * Prefers data-option-value (same escaping as value); falls back to .value.
                 */
                readOptionValueFromInput(el) {
                    if (!el) return '';
                    const fromData = el.getAttribute('data-option-value');
                    if (fromData != null && fromData !== '') return fromData;
                    return el.value;
                }

                getSelectedOptions() {
                    const selected = {};
                    const checkedRadios = this.querySelectorAll('input[type="radio"]:checked');
                    checkedRadios.forEach((el) => {
                        const name = el.dataset.optionName;
                        if (name) selected[name] = this.readOptionValueFromInput(el);
                    });
                    const selects = this.querySelectorAll('select[data-option-name]');
                    selects.forEach((sel) => {
                        const name = sel.dataset.optionName;
                        const opt = sel.selectedOptions[0];
                        if (name && opt) {
                            selected[name] = this.readOptionValueFromInput(opt);
                        }
                    });
                    return selected;
                }

                findProductForSelectedOptions(selectedOptions) {
                    for (const product of this.relatedProducts) {
                        if (!product.options || !product.variants) continue;
                        const relevantVariants = product.variants.filter(variant => 
                            variant.metafields?.custom?.parent_sku === this.parentSku && variant.available
                        );
                        const hasMatch = relevantVariants.some(variant => {
                            for (const [optName, optValue] of Object.entries(selectedOptions)) {
                                const idx = product.options.findIndex(o => o.toLowerCase() === optName.toLowerCase());
                                if (idx === -1) return false;
                                if (!variant.options || variant.options[idx] !== optValue) return false;
                            }
                            return true;
                        });
                        if (hasMatch) return product;
                    }
                    return null;
                }

                findProductForOptionValue(optionName, optionValue) {
                    const selected = this.getSelectedOptions();
                    selected[optionName] = optionValue;
                    return this.findProductForSelectedOptions(selected);
                }

                setupEventListeners() {
                    this.addEventListener('change', this.handleOptionChange.bind(this));
                }

                handleOptionChange(event) {
                    const target = event.target;
                    
                    if (!target.matches('input[type="radio"], select')) {
                        return;
                    }

                    const optionName = target.dataset.optionName;
                    let optionValue;
                    if (target.matches('select')) {
                        const opt = target.selectedOptions[0];
                        optionValue = opt ? this.readOptionValueFromInput(opt) : target.value;
                    } else {
                        optionValue = this.readOptionValueFromInput(target);
                    }
                    const selected = this.getSelectedOptions();
                    selected[optionName] = optionValue;
                    const matchingProduct = this.findProductForSelectedOptions(selected);
                    const productHandle = matchingProduct?.handle;

                    // Update the displayed selected value
                    this.updateSelectedSwatchValue(optionName, optionValue);

                    // Navigate to the matching product
                    if (productHandle && productHandle !== this.currentProduct.handle) {
                        this.navigateToProduct(productHandle);
                    }

                    // Publish event for other components
                    if (typeof window !== 'undefined' && window.FoxTheme?.pubsub) {
                        window.FoxTheme.pubsub.publish(
                            window.FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange,
                            {
                                data: {
                                    event,
                                    target,
                                    optionName,
                                    optionValue,
                                    productHandle
                                },
                            },
                        );
                    }
                }

                updateSelectedSwatchValue(optionName, value) {
                    const selectedValueElement = Array.from(
                        this.querySelectorAll('[data-selected-swatch-value]'),
                    ).find((el) => el.getAttribute('data-selected-swatch-value') === optionName);
                    if (selectedValueElement) {
                        selectedValueElement.textContent = value;
                    }
                }

                navigateToProduct(productHandle) {
                    if (!productHandle) {
                        console.error('No product handle provided for navigation');
                        return;
                    }

                    const productUrl = `/products/${productHandle}`;
                    
                    if (this.updateUrl) {
                        window.location.href = productUrl;
                    } else {
                        // Could implement AJAX loading here
                        window.location.href = productUrl;
                    }
                }
            }
        );
    }
    
    // Export for potential use in other modules
    if (typeof module !== 'undefined' && module.exports) {
        module.exports = { ParentSkuVariantSelects };
    }
}